/**
 * Simple Token Expiration Manager
 * Shows popup 1 minute before token expires
 */

import { JwtPayload } from 'app/pages/Auth/slice/types';
import jwtDecode from 'jwt-decode';

let checkInterval: NodeJS.Timeout | null = null;
let warningShown = false;
let onWarningCallback: (() => void) | null = null;

/**
 * Start monitoring token expiration
 */
export const startTokenMonitoring = (onWarning: () => void): void => {
  onWarningCallback = onWarning;
  warningShown = false;

  // Clear any existing interval
  if (checkInterval) {
    clearInterval(checkInterval);
  }

  // Check every 30 seconds
  checkInterval = setInterval(() => {
    checkTokenExpiration();
  }, 30000);

  // Initial check
  checkTokenExpiration();
};

/**
 * Stop monitoring token expiration
 */
export const stopTokenMonitoring = (): void => {
  if (checkInterval) {
    clearInterval(checkInterval);
    checkInterval = null;
  }
  onWarningCallback = null;
  warningShown = false;
};

/**
 * Check if token will expire soon and show warning
 */
const checkTokenExpiration = (): void => {
  const token = localStorage.getItem('_UFT_');
  if (!token) return;

  try {
    const decoded = jwtDecode<JwtPayload>(token.replace('Bearer ', ''));
    const now = Date.now();
    const expTime = decoded.exp * 1000;
    const timeUntilExp = expTime - now;

    // If token expired, show expired popup instead of auto redirect
    if (timeUntilExp <= 0 && !warningShown && onWarningCallback) {
      warningShown = true;
      onWarningCallback();
      return;
    }

    // Show warning 1 minute before expiration
    if (timeUntilExp <= 60000 && !warningShown && onWarningCallback) {
      warningShown = true;
      onWarningCallback();
    }
  } catch (error) {
    console.error('Token validation failed:', error);
    // Show expired popup for invalid tokens too
    if (!warningShown && onWarningCallback) {
      warningShown = true;
      onWarningCallback();
    }
  }
};

/**
 * Handle expired token - logout user
 */
export const handleExpiredToken = (): void => {
  localStorage.removeItem('_UFT_');
  localStorage.removeItem('ApiKey');
  sessionStorage.removeItem('selectedClientWaNumber');
  window.location.href = '/';
};

/**
 * Check if token is already expired
 */
export const isTokenExpired = (): boolean => {
  const token = localStorage.getItem('_UFT_');
  if (!token) return true;

  try {
    const decoded = jwtDecode<JwtPayload>(token.replace('Bearer ', ''));
    return decoded.exp * 1000 <= Date.now();
  } catch {
    return true;
  }
};

/**
 * Get time until token expires (in milliseconds)
 */
export const getTimeUntilExpiry = (): number => {
  const token = localStorage.getItem('_UFT_');
  if (!token) return 0;

  try {
    const decoded = jwtDecode<JwtPayload>(token.replace('Bearer ', ''));
    return decoded.exp * 1000 - Date.now();
  } catch {
    return 0;
  }
};
